/* eslint-disable no-nested-ternary */
import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Snackbar } from '@mui/material';
import { useAppSelector } from '../../../store/hooks';
import {
  ResetVideoContentList,
  selectVideoContentPageNumber,
  selectGetVideoContentState,
  selectVideoHasMoreContent,
  selectVideoContentList,
  UpdateVideoHasMoreContent,
  UpdateVideoPageNumber,
} from '../../../store/slices/contentSlice';
import { getVideoContent } from '../../../store/thunks/contentThunk';
import ContentHomeSkeleton from './ContentHomeSkeleton';
import { ContentCategoryContainer } from './ContentCategoryContainer';
import { useTrackCategoryContentVisibility } from './useTrackCategoryContentVisibility';
import { useFetchCategoryContent } from './useFetchCategoryContent';
import { Content, ContentFeedSection } from '../../../common/models/content/Content';
import ContentViewMoreContainer from './ContentViewMoreContainer';
import ContentViewMoreSkeleton from './ContentViewMoreSkeleton';

interface VideoContentCategoryProps {
  categoryId: string;
  keyProp: string;
  setSomeContentLoadedInitially: (value: boolean) => void;
  contentFeedSection: ContentFeedSection
  viewMorePage?: boolean
}

export function VideoContentCategory({
  categoryId,
  keyProp,
  viewMorePage = false,
  setSomeContentLoadedInitially,
  contentFeedSection,
}:VideoContentCategoryProps) {
  const storeVideoContentList = useAppSelector(selectVideoContentList);
  const videoContentList = useMemo(() => storeVideoContentList.map((content, indx) => (
    {
      ...content,
      contentFeedSection,
      contentFeedSectionPosition: indx + 1,
    } as Content
  )), [storeVideoContentList, contentFeedSection]);

  const getVideoContentState = useAppSelector(selectGetVideoContentState);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const { t } = useTranslation();
  const videoPage = useAppSelector(selectVideoContentPageNumber);
  const videoHasMore = useAppSelector(selectVideoHasMoreContent);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  useTrackCategoryContentVisibility({
    categoryContentList: videoContentList,
  });

  const fetchMoreCategoryContent = useFetchCategoryContent({
    viewMorePage,
    contentRequestOptions: { contentType: 'video' },
    getCategoryContent: getVideoContent,
    resetCategoryContentList: ResetVideoContentList,
    setSomeContentLoadedInitially,
    setOpenErrorSnackbar,
    setInitialFetchDone,
    updateCategoryPageNumber: UpdateVideoPageNumber,
    updateHasMoreCategoryContent: UpdateVideoHasMoreContent,
  });

  const isLoading = useMemo(
    () => (getVideoContentState === 'pending' && !initialFetchDone),
    [getVideoContentState, initialFetchDone],
  );

  const handleCloseErrorSnackBar = () => {
    setOpenErrorSnackbar(false);
  };

  const onNextPage = useCallback(() => {
    fetchMoreCategoryContent(
      videoPage,
      videoHasMore,
    );
  }, [videoPage, videoHasMore, fetchMoreCategoryContent]);

  return (
    <>
      <Snackbar open={openErrorSnackbar} autoHideDuration={4000} onClose={handleCloseErrorSnackBar}>
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>

      {isLoading ? (
        viewMorePage ? (
          <ContentViewMoreSkeleton />
        ) : (
          <ContentHomeSkeleton key={`${keyProp}-skeleton`} />
        )
      ) : viewMorePage ? (
        <ContentViewMoreContainer
          key={`${categoryId}-content`}
          categoryId={categoryId}
          storeContentList={videoContentList}
          onNextPage={onNextPage}
          hasMoreContent={videoHasMore}
          showSearchBar={true}
        />
      ) : videoContentList?.length > 0 ? (
        <ContentCategoryContainer
          categoryId={categoryId}
          key={`${keyProp}-content`}
          categoryTitle="Videos"
          storeContentList={videoContentList}
          contentFeedSection={contentFeedSection}
        />
      ) : null}
    </>
  );
}
